import { Injectable } from '@angular/core';
import { DateValidationService } from '../../services/date-validation.service';
import { parseLocalDateString } from '../../helpers/date-utils';

export interface MedicalHistoryDiscrepancy {
  field: string;
  category: string;
  savedValue: any;
  currentValue: any;
  savedId?: string;
  currentId?: string;
  savedDate?: string;
  currentDate?: string;
  savedStopDate?: string;
  currentStopDate?: string;
  savedFreq?: string;
  currentFreq?: string;
  savedDosage?: string;
  currentDosage?: string;
}

export interface DemographicsDiscrepancy {
  field: string;
  saved: any;
  current: any;
  widgetId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class DiscrepancyDetectionService {
  constructor(private dateValidationService: DateValidationService) {}
  detectDemographicsDiscrepancies(
    savedFormData: any,
    currentCustomer: any,
  ): DemographicsDiscrepancy[] {
    const discrepancies: DemographicsDiscrepancy[] = [];

    if (!savedFormData || !currentCustomer) {
      return discrepancies;
    }

    const savedDemographicsWidget = this.findDemographicsWidget(savedFormData);

    if (!savedDemographicsWidget) {
      return discrepancies;
    }

    const savedItems = savedDemographicsWidget.properties?.items?.value || [];

    const personalInfo = savedItems.find(
      (item) => item.title === 'Personal Information' && item.nested,
    );
    if (personalInfo?.items) {
      const savedFirstName =
        personalInfo.items.find((field) => field.title === 'First Name')?.value || '';
      if (
        savedFirstName &&
        currentCustomer.FirstName &&
        savedFirstName !== currentCustomer.FirstName
      ) {
        discrepancies.push({
          field: 'First Name',
          saved: savedFirstName,
          current: currentCustomer.FirstName,
          widgetId: savedDemographicsWidget.id,
        });
      }

      const savedLastName =
        personalInfo.items.find((field) => field.title === 'Last Name')?.value || '';
      if (savedLastName && currentCustomer.LastName && savedLastName !== currentCustomer.LastName) {
        discrepancies.push({
          field: 'Last Name',
          saved: savedLastName,
          current: currentCustomer.LastName,
          widgetId: savedDemographicsWidget.id,
        });
      }

      const savedMiddleName =
        personalInfo.items.find((field) => field.title === 'Middle Name')?.value || '';
      if (
        savedMiddleName &&
        currentCustomer.MiddleName &&
        savedMiddleName !== currentCustomer.MiddleName
      ) {
        discrepancies.push({
          field: 'Middle Name',
          saved: savedMiddleName,
          current: currentCustomer.MiddleName,
          widgetId: savedDemographicsWidget.id,
        });
      }

      const savedGender = personalInfo.items.find((field) => field.title === 'Gender')?.value || '';
      if (savedGender && currentCustomer.Gender && savedGender !== currentCustomer.Gender) {
        const genderDisplayMap: { [key: string]: string } = {
          M: 'Male',
          F: 'Female',
          U: 'Other',
          X: 'Unknown',
        };
        discrepancies.push({
          field: 'Gender',
          saved: genderDisplayMap[savedGender] || savedGender,
          current: genderDisplayMap[currentCustomer.Gender] || currentCustomer.Gender,
          widgetId: savedDemographicsWidget.id,
        });
      }

      const savedBirthday =
        personalInfo.items.find((field) => field.title === 'Birthday')?.value || '';
      if (
        savedBirthday &&
        currentCustomer.Birthday &&
        !this.dateValidationService.shouldExcludeDate(currentCustomer.Birthday)
      ) {
        const currentFormatted = parseLocalDateString(currentCustomer.Birthday);

        if (savedBirthday !== currentFormatted) {
          discrepancies.push({
            field: 'Birthday',
            saved: `${savedBirthday}`,
            current: `${currentFormatted}`,
            widgetId: savedDemographicsWidget.id,
          });
        }
      }
    }

    const savedGender =
      savedItems.find((item) => item.title === 'Gender' && !item.nested)?.value || '';
    if (savedGender && currentCustomer.Gender && savedGender !== currentCustomer.Gender) {
      const genderDisplayMap: { [key: string]: string } = {
        M: 'Male',
        F: 'Female',
        U: 'Other',
        X: 'Unknown',
      };
      discrepancies.push({
        field: 'Gender',
        saved: genderDisplayMap[savedGender] || savedGender,
        current: genderDisplayMap[currentCustomer.Gender] || currentCustomer.Gender,
        widgetId: savedDemographicsWidget.id,
      });
    }

    const savedBirthday =
      savedItems.find((item) => item.title === 'Birthday' && !item.nested)?.value || '';
    if (
      savedBirthday &&
      currentCustomer.Birthday &&
      !this.dateValidationService.shouldExcludeDate(currentCustomer.Birthday)
    ) {
      const currentFormatted = parseLocalDateString(currentCustomer.Birthday);

      if (savedBirthday !== currentFormatted) {
        discrepancies.push({
          field: 'Birthday',
          saved: `${savedBirthday}`,
          current: `${currentFormatted}`,
          widgetId: savedDemographicsWidget.id,
        });
      }
    }

    const addressInfo = savedItems.find((item) => item.title === 'Address' && item.nested);
    if (addressInfo?.items) {
      const savedAddress1 =
        addressInfo.items.find((field) => field.title === 'Address 1')?.value || '';
      if (savedAddress1 && currentCustomer.Address && savedAddress1 !== currentCustomer.Address) {
        discrepancies.push({
          field: 'Address 1',
          saved: savedAddress1,
          current: currentCustomer.Address,
          widgetId: savedDemographicsWidget.id,
        });
      }

      const savedAddress2 =
        addressInfo.items.find((field) => field.title === 'Address 2')?.value || '';
      if (savedAddress2 && currentCustomer.Address2 && savedAddress2 !== currentCustomer.Address2) {
        discrepancies.push({
          field: 'Address 2',
          saved: savedAddress2,
          current: currentCustomer.Address2,
          widgetId: savedDemographicsWidget.id,
        });
      }

      const savedCity = addressInfo.items.find((field) => field.title === 'City')?.value || '';
      if (savedCity && currentCustomer.City && savedCity !== currentCustomer.City) {
        discrepancies.push({
          field: 'City',
          saved: savedCity,
          current: currentCustomer.City,
          widgetId: savedDemographicsWidget.id,
        });
      }

      const savedState = addressInfo.items.find((field) => field.title === 'State')?.value || '';
      if (
        savedState &&
        currentCustomer.State &&
        savedState !== currentCustomer.State?.toUpperCase()
      ) {
        discrepancies.push({
          field: 'State',
          saved: savedState,
          current: currentCustomer.State,
          widgetId: savedDemographicsWidget.id,
        });
      }

      const savedZip = addressInfo.items.find((field) => field.title === 'Zip/Postal')?.value || '';
      if (savedZip && currentCustomer.Zipcode && savedZip !== currentCustomer.Zipcode) {
        discrepancies.push({
          field: 'Zip/Postal',
          saved: savedZip,
          current: currentCustomer.Zipcode,
          widgetId: savedDemographicsWidget.id,
        });
      }
    }

    // Check Contact Information
    const contactInfo = savedItems.find(
      (item) => item.title === 'Contact Information' && item.nested,
    );
    if (contactInfo?.items) {
      const savedMobilePhone =
        contactInfo.items.find((field) => field.title === 'Mobile Phone')?.value || '';
      if (
        savedMobilePhone &&
        currentCustomer.MobilePhone &&
        savedMobilePhone !== currentCustomer.MobilePhone
      ) {
        discrepancies.push({
          field: 'Mobile Phone',
          saved: savedMobilePhone,
          current: currentCustomer.MobilePhone,
          widgetId: savedDemographicsWidget.id,
        });
      }

      const savedEmail = contactInfo.items.find((field) => field.title === 'Email')?.value || '';
      if (savedEmail && currentCustomer.Email && savedEmail !== currentCustomer.Email) {
        discrepancies.push({
          field: 'Email',
          saved: savedEmail,
          current: currentCustomer.Email,
          widgetId: savedDemographicsWidget.id,
        });
      }
    }

    return discrepancies;
  }

  private findDemographicsWidget(formData: any): any {
    if (typeof formData === 'string') {
      try {
        formData = JSON.parse(formData);
      } catch (e) {
        return null;
      }
    }

    if (formData.sections) {
      for (const section of formData.sections) {
        for (const widget of section.widgets || []) {
          if (widget.name === 'fb-emr-demographics') {
            return widget;
          }
        }
      }
    }

    return null;
  }

  detectMedicalHistoryDiscrepancies(
    savedFormData: any,
    currentMedicalHistory: any,
  ): MedicalHistoryDiscrepancy[] {
    const discrepancies: MedicalHistoryDiscrepancy[] = [];

    if (!savedFormData || !currentMedicalHistory) {
      return discrepancies;
    }

    const savedMedicalHistoryWidget = this.findMedicalHistoryWidget(savedFormData);

    if (!savedMedicalHistoryWidget) {
      return discrepancies;
    }

    const savedItems = savedMedicalHistoryWidget.properties?.items?.value || [];

    const hasAnySavedData = savedItems.some((item) => {
      const value = item.value;
      if (Array.isArray(value)) {
        return value.some((v) => {
          if (typeof v === 'object' && v !== null) {
            return Object.values(v).some((val) => val && val !== '');
          }
          return v && v !== '';
        });
      }
      return value && value !== '' && value !== false;
    });

    if (!hasAnySavedData) {
      return [];
    }

    this.checkChronicConditions(savedItems, currentMedicalHistory.chronicConditions, discrepancies);
    this.checkAllergies(savedItems, currentMedicalHistory.allergies, discrepancies);
    this.checkMedications(savedItems, currentMedicalHistory.medications, discrepancies);
    this.checkSurgeries(savedItems, currentMedicalHistory.surgeries, discrepancies);

    return discrepancies;
  }

  private findMedicalHistoryWidget(formData: any): any {
    if (typeof formData === 'string') {
      try {
        formData = JSON.parse(formData);
      } catch (e) {
        return null;
      }
    }

    if (formData.sections) {
      for (const section of formData.sections) {
        for (const widget of section.widgets || []) {
          if (widget.name === 'fb-emr-medical-history') {
            return widget;
          }
        }
      }
    }

    return null;
  }

  private checkChronicConditions(
    savedItems: any[],
    currentConditions: any[],
    discrepancies: MedicalHistoryDiscrepancy[],
  ) {
    const savedConditionsItem = savedItems.find((item) => item.name === 'other_chronic_conditions');
    const savedConditions = savedConditionsItem?.value || [];

    const savedConditionMap = new Map<string, any>();
    const savedConditionsWithoutId = [];

    if (!Array.isArray(savedConditions)) {
      console.warn('savedConditions is not an array:', savedConditions);
      return;
    }

    savedConditions.forEach((condition) => {
      const conditionField = condition?.[1];
      const conditionId = conditionField?.id;

      const parsedDate = parseLocalDateString(condition?.[0]?.value);

      if (conditionId) {
        savedConditionMap.set(String(conditionId), {
          name: conditionField?.value,
          date: parsedDate,
        });
      } else if (conditionField?.value) {
        savedConditionsWithoutId.push({
          name: conditionField?.value,
          date: parsedDate,
        });
      }
    });

    const currentConditionMap = new Map<string, any>();

    currentConditions?.forEach((condition) => {
      // Only use the Id field (not ChronicId)
      const id = condition.Id;

      if (id) {
        currentConditionMap.set(String(id), {
          name: condition.ChronicName,
          date: condition.OnsetDate,
        });
      }
    });

    savedConditionMap.forEach((savedCondition, savedId) => {
      const currentCondition = currentConditionMap.get(savedId);
      if (!currentCondition) {
        // Condition not in API - not a discrepancy
      } else {
        const nameChanged = savedCondition.name !== currentCondition.name;
        const dateChanged =
          savedCondition.date &&
          currentCondition.date &&
          savedCondition.date !== parseLocalDateString(currentCondition.date);

        if (nameChanged || dateChanged) {
          // Build display showing only what changed
          let savedDisplay = '';
          let currentDisplay = '';

          if (nameChanged && dateChanged) {
            // Both changed - show both
            savedDisplay = savedCondition.date
              ? `${savedCondition.name} | ${savedCondition.date}`
              : savedCondition.name;
            currentDisplay = currentCondition.date
              ? `${currentCondition.name} | ${parseLocalDateString(currentCondition.date)}`
              : currentCondition.name;
          } else if (nameChanged) {
            // Only name changed
            savedDisplay = savedCondition.name || '';
            currentDisplay = currentCondition.name || '';
          } else if (dateChanged) {
            // Only date changed
            savedDisplay = savedCondition.date || '';
            currentDisplay = currentCondition.date
              ? parseLocalDateString(currentCondition.date)
              : '';
          }

          discrepancies.push({
            field: `Chronic Condition - ${savedCondition.name}`,
            category: 'other_chronic_conditions',
            savedId: savedId,
            currentId: savedId,
            savedValue: savedDisplay,
            currentValue: currentDisplay,
          });
        }
      }
    });
  }

  private checkAllergies(
    savedItems: any[],
    currentAllergies: any[],
    discrepancies: MedicalHistoryDiscrepancy[],
  ) {
    const savedAllergiesItem = savedItems.find((item) => item.name === 'allergies');
    const savedAllergies = savedAllergiesItem?.value || [];

    if (!Array.isArray(savedAllergies)) {
      return;
    }

    // Build map of saved allergies with IDs
    const savedAllergyMap = new Map<string, any>();

    savedAllergies.forEach((allergy) => {
      const [dateField, allergyField] = allergy || [];
      const allergyId = allergyField?.id;
      const allergyName = allergyField?.value;

      if (allergyId) {
        savedAllergyMap.set(String(allergyId), {
          name: allergyName,
          date: parseLocalDateString(dateField?.value),
        });
      } else if (allergyName) {
        // Manual entries without IDs - not discrepancies
      }
    });

    // Build map of current allergies from API
    const currentAllergyMap = new Map<string, any>();

    currentAllergies?.forEach((allergy) => {
      // Only use the Id field (not AllergyId)
      const id = allergy.Id;
      if (id) {
        currentAllergyMap.set(String(id), {
          name: allergy.AllergyName,
          date: allergy.DiagDate,
        });
      }
    });

    savedAllergyMap.forEach((savedAllergy, savedId) => {
      const currentAllergy = currentAllergyMap.get(savedId);

      if (!currentAllergy) {
        // Allergy not in API - not a discrepancy
      } else {
        // Check if either name or date has discrepancy
        const nameChanged = savedAllergy.name !== currentAllergy.name;
        const dateChanged =
          savedAllergy.date &&
          currentAllergy.date &&
          savedAllergy.date !== parseLocalDateString(currentAllergy.date);

        if (nameChanged || dateChanged) {
          // Build display showing only what changed
          let savedDisplay = '';
          let currentDisplay = '';

          if (nameChanged && dateChanged) {
            // Both changed - show both
            savedDisplay = savedAllergy.date
              ? `${savedAllergy.name} | ${savedAllergy.date}`
              : savedAllergy.name;
            currentDisplay = currentAllergy.date
              ? `${currentAllergy.name} | ${parseLocalDateString(currentAllergy.date)}`
              : currentAllergy.name;
          } else if (nameChanged) {
            // Only name changed
            savedDisplay = savedAllergy.name || '';
            currentDisplay = currentAllergy.name || '';
          } else if (dateChanged) {
            // Only date changed
            savedDisplay = savedAllergy.date || '';
            currentDisplay = currentAllergy.date ? parseLocalDateString(currentAllergy.date) : '';
          }

          discrepancies.push({
            field: `Allergy - ${savedAllergy.name}`,
            category: 'allergies',
            savedId: savedId,
            currentId: savedId,
            savedValue: savedDisplay,
            currentValue: currentDisplay,
          });
        }
      }
    });
  }

  private checkMedications(
    savedItems: any[],
    currentMedications: any[],
    discrepancies: MedicalHistoryDiscrepancy[],
  ) {
    const savedMedicationsItem = savedItems.find((item) => item.name === 'medications');
    const savedMedications = savedMedicationsItem?.value || [];

    const savedMedicationMap = new Map<string, any>();
    const savedMedicationsByName = new Map<string, any>();
    const savedMedicationsWithoutId = [];

    if (!Array.isArray(savedMedications)) {
      return;
    }

    // Process saved medications
    savedMedications.forEach((medication) => {
      // Handle both formats: medication.id and medication.Id
      const medicationId = medication?.id || medication?.Id;
      // Handle both formats: medication.Medication and medication.MedicationName
      const medicationName = medication?.Medication || medication?.MedicationName || '';

      if (medicationId) {
        savedMedicationMap.set(String(medicationId), {
          name: medicationName,
          date: parseLocalDateString(medication?.Date || medication?.StartDate),
          stopDate: parseLocalDateString(medication?.Stop || medication?.StopDate),
          freq: medication?.Freq || medication?.frequency || '',
          dosage: medication?.Dosage || medication?.dosage || '',
        });
      }

      // Also store by normalized name for name-based matching
      if (medicationName) {
        const normalizedName = medicationName.toLowerCase().trim();
        savedMedicationsByName.set(normalizedName, {
          id: medicationId,
          name: medicationName,
          date: parseLocalDateString(medication?.Date || medication?.StartDate),
          freq: medication?.Freq,
          dosage: medication?.Dosage,
        });

        if (!medicationId) {
          savedMedicationsWithoutId.push({
            name: medicationName,
            date: parseLocalDateString(medication?.Date || medication?.StartDate),
            freq: medication?.Freq,
            dosage: medication?.Dosage,
          });
        }
      }
    });

    const currentMedicationMap = new Map<string, any>();
    const currentMedicationsByName = new Map<string, any>();

    currentMedications?.forEach((medication) => {
      // Only use the Id field (not MedicationId)
      const id = medication.Id;
      const medicationName = medication.MedicationName || '';

      if (id) {
        currentMedicationMap.set(String(id), {
          name: medicationName,
          date: medication.StartDate,
          stopDate: medication.StopDate,
          freq: medication.Freq || '',
          dosage: medication.Dosage || '',
        });
      }

      // Also store by normalized name for name-based matching
      if (medicationName) {
        const normalizedName = medicationName.toLowerCase().trim();
        currentMedicationsByName.set(normalizedName, {
          id: id,
          name: medicationName,
          date: medication.StartDate,
          freq: medication.Freq,
          dosage: medication.Dosage,
        });
      }
    });

    savedMedicationMap.forEach((savedMedication, savedId) => {
      const currentMedication = currentMedicationMap.get(savedId);

      if (!currentMedication) {
        // Medication not in API - not a discrepancy`);
      } else {
        const differences = [];

        // Check name
        if (
          savedMedication.name.toLowerCase().trim() !== currentMedication.name.toLowerCase().trim()
        ) {
          differences.push('Name');
        }

        // Check dates
        const savedStartDate = savedMedication.date || '';
        const currentStartDate = currentMedication.date
          ? parseLocalDateString(currentMedication.date)
          : '';
        if (savedStartDate && currentStartDate && savedStartDate !== currentStartDate) {
          differences.push('Start');
        }

        // Check Stop Date
        const savedStopDate = savedMedication.stopDate || '';
        const currentStopDate = currentMedication.stopDate
          ? parseLocalDateString(currentMedication.stopDate)
          : '';
        const isValidStopDate = (date: string) => date && !date.startsWith('0001');
        if (
          isValidStopDate(savedStopDate) &&
          isValidStopDate(currentStopDate) &&
          savedStopDate !== currentStopDate
        ) {
          differences.push('Stop');
        }

        // Check frequency - only mark as different if BOTH have values and they differ
        const savedFreq = (savedMedication.freq || '').trim();
        const currentFreq = (currentMedication.freq || '').trim();
        if (savedFreq && currentFreq && savedFreq !== currentFreq) {
          differences.push('Freq');
        }

        // Check dosage - only mark as different if BOTH have values and they differ
        const savedDosage = (savedMedication.dosage || '').trim();
        const currentDosage = (currentMedication.dosage || '').trim();
        if (savedDosage && currentDosage && savedDosage !== currentDosage) {
          differences.push('Dosage');
        }

        // If there are any differences, create a single grouped discrepancy
        if (differences.length > 0) {
          let savedDisplay = '';
          let currentDisplay = '';

          // Build display based on what changed
          const changedParts = [];

          // Check what changed and build display accordingly
          if (differences.includes('Name')) {
            savedDisplay = savedMedication.name;
            currentDisplay = currentMedication.name;
          } else {
            // Name didn't change, show only changed fields
            const savedParts = [];
            const currentParts = [];

            if (differences.includes('Start') || differences.includes('Stop')) {
              // Date range changed - only show what's different
              if (differences.includes('Start') && differences.includes('Stop')) {
                // Both dates changed - show full range
                const formatDateRange = (start: string, stop: string) => {
                  if (isValidStopDate(stop)) {
                    return start ? `${start} to ${stop}` : `to ${stop}`;
                  }
                  return start || '';
                };
                savedParts.push(formatDateRange(savedStartDate, savedStopDate));
                currentParts.push(formatDateRange(currentStartDate, currentStopDate));
              } else if (differences.includes('Start')) {
                // Only start date changed - check if stop dates exist and are same
                if (isValidStopDate(savedStopDate) && savedStopDate === currentStopDate) {
                  // Stop dates are the same, only show start dates
                  if (savedStartDate) savedParts.push(savedStartDate);
                  if (currentStartDate) currentParts.push(currentStartDate);
                } else if (isValidStopDate(savedStopDate) || isValidStopDate(currentStopDate)) {
                  // Stop dates exist but differ, show full range
                  const formatDateRange = (start: string, stop: string) => {
                    if (isValidStopDate(stop)) {
                      return start ? `${start} to ${stop}` : `to ${stop}`;
                    }
                    return start || '';
                  };
                  const savedRange = formatDateRange(savedStartDate, savedStopDate);
                  const currentRange = formatDateRange(currentStartDate, currentStopDate);
                  if (savedRange) savedParts.push(savedRange);
                  if (currentRange) currentParts.push(currentRange);
                } else {
                  // No stop dates, just show start
                  if (savedStartDate) savedParts.push(savedStartDate);
                  if (currentStartDate) currentParts.push(currentStartDate);
                }
              } else if (differences.includes('Stop')) {
                // Only stop date changed
                if (savedStopDate) savedParts.push(`ends ${savedStopDate}`);
                if (currentStopDate) currentParts.push(`ends ${currentStopDate}`);
              }
            }

            // Handle frequency and dosage together
            const freqDosageChanged =
              differences.includes('Freq') || differences.includes('Dosage');
            if (freqDosageChanged) {
              const savedFreqDosage = [];
              const currentFreqDosage = [];

              // Add frequency if it exists or differs
              if (differences.includes('Freq')) {
                if (savedMedication.freq) savedFreqDosage.push(savedMedication.freq);
                if (currentMedication.freq) currentFreqDosage.push(currentMedication.freq);
              }

              // Add dosage if it exists or differs
              if (differences.includes('Dosage')) {
                if (savedMedication.dosage) savedFreqDosage.push(savedMedication.dosage);
                if (currentMedication.dosage) currentFreqDosage.push(currentMedication.dosage);
              }

              // Only add to display if there's something to show
              if (savedFreqDosage.length > 0) {
                savedParts.push(savedFreqDosage.join(' | '));
              }

              if (currentFreqDosage.length > 0) {
                currentParts.push(currentFreqDosage.join(' | '));
              }
            }

            // Join all parts
            savedDisplay = savedParts.join(' | ');
            currentDisplay = currentParts.join(' | ');
          }

          discrepancies.push({
            field: `Medication - ${savedMedication.name}`,
            category: 'medications',
            savedId: savedId,
            currentId: savedId,
            savedValue: savedDisplay,
            currentValue: currentDisplay,
          });
        }
      }
    });
  }

  private checkSurgeries(
    savedItems: any[],
    currentSurgeries: any[],
    discrepancies: MedicalHistoryDiscrepancy[],
  ) {
    const savedSurgeriesItem = savedItems.find((item) => item.name === 'surgeries');
    const savedSurgeries = savedSurgeriesItem?.value || [];

    const savedSurgeryMap = new Map<string, any>();
    const savedSurgeriesWithoutId = [];

    if (!Array.isArray(savedSurgeries)) {
      return;
    }

    savedSurgeries.forEach((surgery) => {
      const surgeryField = surgery?.[1];
      const surgeryId = surgeryField?.id;
      if (surgeryId) {
        savedSurgeryMap.set(String(surgeryId), {
          name: surgeryField?.value,
          date: parseLocalDateString(surgery?.[0]?.value),
        });
      } else if (surgeryField?.value) {
        savedSurgeriesWithoutId.push({
          name: surgeryField?.value,
          date: parseLocalDateString(surgery?.[0]?.value),
        });
      }
    });

    const currentSurgeryMap = new Map<string, any>();
    currentSurgeries?.forEach((surgery) => {
      // Only use the Id field (not SurgeryId)
      const id = surgery.Id;
      if (id) {
        currentSurgeryMap.set(String(id), {
          name: surgery.SurgeryName,
          date: surgery.SurgeryDate,
        });
      }
    });

    savedSurgeryMap.forEach((savedSurgery, savedId) => {
      const currentSurgery = currentSurgeryMap.get(savedId);
      if (!currentSurgery) {
        // Surgery not in API - not a discrepancy
      } else {
        // Check if either name or date has discrepancy
        const nameChanged = savedSurgery.name !== currentSurgery.name;
        const dateChanged =
          savedSurgery.date &&
          currentSurgery.date &&
          savedSurgery.date !== parseLocalDateString(currentSurgery.date);

        if (nameChanged || dateChanged) {
          // Build display showing only what changed
          let savedDisplay = '';
          let currentDisplay = '';

          if (nameChanged && dateChanged) {
            // Both changed - show both
            savedDisplay = savedSurgery.date
              ? `${savedSurgery.name} | ${savedSurgery.date}`
              : savedSurgery.name;
            currentDisplay = currentSurgery.date
              ? `${currentSurgery.name} | ${parseLocalDateString(currentSurgery.date)}`
              : currentSurgery.name;
          } else if (nameChanged) {
            // Only name changed
            savedDisplay = savedSurgery.name || '';
            currentDisplay = currentSurgery.name || '';
          } else if (dateChanged) {
            // Only date changed
            savedDisplay = savedSurgery.date || '';
            currentDisplay = currentSurgery.date ? parseLocalDateString(currentSurgery.date) : '';
          }

          discrepancies.push({
            field: `Surgery - ${savedSurgery.name}`,
            category: 'surgeries',
            savedId: savedId,
            currentId: savedId,
            savedValue: savedDisplay,
            currentValue: currentDisplay,
          });
        }
      }
    });
  }
}
